import { EventItem } from "./components/EventItem";
import styles from "./Planner.module.scss";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Icon, IconType } from "../../../common/icons/Icon";
import { ItemsList } from "../../../common/components/itemsList/ItemsList";
import { routePaths } from "../../../common/infrastructure/routes/routePaths";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { IPlannerEventModel } from "./models/plannerEventModel";
import { usePlanner } from "./hooks/usePlanner";
import {
  canDeletePlannerEvent,
  canEditPlannerEvent,
  canRestorePlannerEvent,
} from "./utils/plannerEventActionsUtils";
import { FilterIconButton } from "../../../common/components/filters/FilterIconButton";
import { FiltersSidePanel } from "../../../common/components/filters/FiltersSidePanel";
import { PlannerFiltersSidebar } from "./components/PlannerFiltersSidebar";
import { Tag } from "../../../common/components/common/Tag/Tag";
import { EventTypeLabels } from "../../../common/labels";
import { EventType } from "../../../common/enums";
import {
  ALL_HALLS_OPTION_ID,
  isAllHallsSelected,
} from "./models/plannerFilters";
import { AppDropdownMenu } from "../../../common/components/AppDropdownMenu/AppDropdownMenu";
import { AppDialog } from "../../../common/components/AppDialog/AppDialog";
import { FormProvider } from "react-hook-form";
import { DefaultInputField } from "../../../common/components/forms/inputs/InputField/DefaultInputField";
import { ColorButton } from "../../../common/components/button/colorButton/ColorButton";
import { BulkAssignInstructorDialog } from "./components/BulkAssignInstructorDialog";
import { BulkDeleteEventsDialog } from "./components/BulkDeleteEventsDialog";
import { HandoverNotePin } from "./components/HandoverNotePin";
import { HandoverNoteButton } from "./components/HandoverNoteButton";
import { HandoverNoteDialog } from "./components/HandoverNoteDialog";
import { classNames } from "primereact/utils";
import { RenderWhen } from "../../../common/infrastructure/components/RenderWhen";

// Comes from the common calendar router
export const Planner = () => {
  const {
    isLoading,
    eventsForCurrentDate,
    selectedValue,
    studioEmployees,
    studioHalls,
    tenantUsesMoreThanOneHall,
    onEventClick,
    handleActiveStartDateChange,
    tileContent,
    handleDateChange,
    filters,
    updateFilters,
    clearAllFilters,
    hasActiveFilters,
    totalActiveFilters,
    handleDeleteEvent,
    // Filters sidebar
    isFiltersSidebarOpen,
    openFiltersSidebar,
    closeFiltersSidebar,
    // Search dialog
    isSearchDialogOpen,
    openSearchDialog,
    closeSearchDialog,
    searchFormMethods,
    handleSearchReservation,
    isSearching,
    // Bulk instructor assignment dialog
    canBulkAssignInstructor,
    isBulkAssignInstructorDialogOpen,
    openBulkAssignInstructorDialog,
    closeBulkAssignInstructorDialog,
    // Bulk delete dialog
    canBulkDeleteEvents,
    isBulkDeleteEventsDialogOpen,
    openBulkDeleteEventsDialog,
    closeBulkDeleteEventsDialog,
    // Mobile / tablet "take the whole planner" panel
    canExpandPanel,
    isPanelExpanded,
    isPanelAnimating,
    togglePanelExpanded,
    panelRef,
    plannerAreaRef,
    panelStyle,
    // Hand-over note for the selected day
    handover,
  } = usePlanner();

  const removeHall = (hallId: number) => {
    const remaining = filters.selectedHallIds.filter(
      (id) => id !== hallId && id !== ALL_HALLS_OPTION_ID,
    );
    updateFilters({
      selectedHallIds: remaining.length > 0 ? remaining : [ALL_HALLS_OPTION_ID],
    });
  };

  const selectedHallTags =
    tenantUsesMoreThanOneHall && !isAllHallsSelected(filters.selectedHallIds)
      ? filters.selectedHallIds
          .filter((id) => id !== ALL_HALLS_OPTION_ID)
          .map((id) => ({
            id,
            name: studioHalls?.find((h) => h.id === id)?.name ?? `#${id}`,
          }))
      : [];

  const removeInstructor = (instructorId: number) => {
    updateFilters({
      selectedInstructors: filters.selectedInstructors.filter(
        (inst) => inst.id !== instructorId,
      ),
    });
  };

  const removeEventType = (eventType: EventType) => {
    updateFilters({
      selectedEventTypes: filters.selectedEventTypes.filter(
        (type) => type !== eventType,
      ),
    });
  };

  const handoverPin = (
    <HandoverNotePin
      entriesCount={handover.entriesCount}
      hasUnread={handover.hasUnread}
      unreadCount={handover.unreadCount}
      previewContent={handover.previewContent}
      previewAuthor={handover.previewAuthor}
      onClick={handover.openDialog}
    />
  );

  const header = (
    <div className={styles.header_wrapper}>
      <div className={styles.header_title}>
        <div className={styles.header_title_left}>
          <span className={styles.header_title_text}>График</span>
          <RenderWhen condition={canExpandPanel}>
            <button
              type="button"
              className={classNames(styles.header_expandButton, {
                [styles.header_expandButton_open]: isPanelExpanded,
              })}
              onClick={togglePanelExpanded}
              aria-label={
                isPanelExpanded
                  ? "Върни графика на мястото му"
                  : "Покажи графика на цял екран"
              }
              aria-pressed={isPanelExpanded}
            >
              <Icon
                type={isPanelExpanded ? IconType.Collapse : IconType.Expand}
                width="18px"
                height="18px"
              />
            </button>
          </RenderWhen>
        </div>
        <div className={styles.filterButtonWrapper}>
          <FilterIconButton
            onClick={openFiltersSidebar}
            activeFiltersCount={totalActiveFilters}
          />
          <AppDropdownMenu
            triggerIcon={IconType.MoreOptions}
            triggerIconSize={"24px"}
            triggerIconColor={"#fff"}
            items={[
              {
                label: "Търсене по номер на резервация",
                icon: IconType.Search,
                onClick: openSearchDialog,
              },
              ...(canBulkAssignInstructor
                ? [
                    {
                      label: "Прехвърли класове на инструктор",
                      icon: IconType.EditIcon,
                      onClick: openBulkAssignInstructorDialog,
                    },
                  ]
                : []),
              ...(canBulkDeleteEvents
                ? [
                    {
                      label: "Изтрий няколко събития",
                      icon: IconType.DeleteIcon,
                      onClick: openBulkDeleteEventsDialog,
                    },
                  ]
                : []),
            ]}
          />
        </div>
      </div>

      {hasActiveFilters && (
        <div className={styles.filtersContainer}>
          {/* Instructor Filters */}
          {filters.selectedInstructors.length > 0 && (
            <div className={styles.filterSection}>
              <span className={styles.sectionLabel}>Служители:</span>
              {filters.selectedInstructors.map((instructor) => (
                <Tag
                  key={instructor.id}
                  label={instructor.name}
                  variant="instructor"
                  removable
                  onRemove={() => removeInstructor(instructor.id)}
                />
              ))}
            </div>
          )}

          {/* Hall Filters */}
          {selectedHallTags.length > 0 && (
            <div className={styles.filterSection}>
              <span className={styles.sectionLabel}>Зали:</span>
              {selectedHallTags.map((hall) => (
                <Tag
                  key={hall.id}
                  label={hall.name}
                  variant="instructor"
                  removable
                  onRemove={() => removeHall(hall.id)}
                />
              ))}
            </div>
          )}

          {/* Event Type Filters */}
          {filters.selectedEventTypes.length > 0 && (
            <div className={styles.filterSection}>
              <span className={styles.sectionLabel}>Типове:</span>
              {filters.selectedEventTypes.map((eventType) => (
                <Tag
                  key={eventType}
                  label={EventTypeLabels[eventType]}
                  variant="eventType"
                  removable
                  onRemove={() => removeEventType(eventType)}
                />
              ))}
            </div>
          )}

          {/* Show Deleted Events Filter */}
          {filters.showDeletedEvents && (
            <div className={styles.filterSection}>
              <span className={styles.sectionLabel}>Други:</span>
              <Tag
                label="Изтрити събития"
                variant="eventType"
                removable
                onRemove={() => updateFilters({ showDeletedEvents: false })}
              />
            </div>
          )}

          {/* Clear All Button */}
          <div onClick={clearAllFilters} className={styles.clearAllTag}>
            <Tag label="Изчисти всички" variant="clearAll" />
          </div>
        </div>
      )}

      {/* A day that holds notes earns a full-width card of its own, pinned above the
          classes rather than injected into them: the list owns search, swipe actions
          and virtualisation, none of which a note belongs in. An empty day has nothing
          to fill that width with, so it gets the round button on the search row. */}
      <RenderWhen condition={handover.hasEntries}>{handoverPin}</RenderWhen>
    </div>
  );

  return (
    <div className={styles.root} ref={plannerAreaRef}>
      <Calendar
        className={classNames(styles.calendar, {
          [styles.calendar_dimmed]: isPanelExpanded,
        })}
        tileClassName={styles.calendar_tile}
        onChange={handleDateChange}
        value={selectedValue}
        tileContent={tileContent}
        locale="BG"
        onActiveStartDateChange={handleActiveStartDateChange}
      />

      <div
        ref={panelRef}
        style={panelStyle}
        className={classNames(styles.panel, {
          [styles.panel_expanded]: isPanelExpanded,
          [styles.panel_animating]: isPanelAnimating,
        })}
      >
        <ItemsList<IPlannerEventModel>
          className={styles.items}
          isLoading={isLoading}
          numberOfItems={15}
          enableSearch
          searchSuffix={
            handover.hasEntries ? undefined : (
              <HandoverNoteButton onClick={handover.openDialog} />
            )
          }
          fullHeight
          fullWidth
          title={header}
          noItemsText="Няма открити класове за тази дата."
          items={eventsForCurrentDate ?? []}
          itemTemplate={(item, index, actionButtons) => {
            return (
              <EventItem
                item={item}
                isLoading={isLoading}
                onEventClick={onEventClick}
                key={item?.id ?? index}
                actionButtons={actionButtons}
              />
            );
          }}
          swipeLeftActions={[
            {
              type: "onClick",
              iconType: IconType.DeleteIcon,
              onClick: handleDeleteEvent,
              validator: canDeletePlannerEvent,
            },
            {
              enablePrompt: true,
              type: "other",
              promptMessage:
                "Сигурни ли сте, че искате да възстановите това събитие?",
              iconType: IconType.PutBackIcon,
              method: "put",
              endpoint: endpointsMetadata.planner.putBackEvent,
              validator: canRestorePlannerEvent,
              invalidateKeys: [[endpointsMetadata.planner.getAll]],
            },
          ]}
          swipeRightActions={[
            {
              type: "edit",
              iconType: IconType.EditIcon,
              endpoint: routePaths.plannerEdit,
              validator: canEditPlannerEvent,
            },
          ]}
        />
      </div>

      <FiltersSidePanel
        visible={isFiltersSidebarOpen}
        onHide={closeFiltersSidebar}
      >
        <PlannerFiltersSidebar
          filters={filters}
          studioEmployees={studioEmployees}
          studioHalls={studioHalls}
          tenantUsesMoreThanOneHall={tenantUsesMoreThanOneHall}
          onFiltersChange={updateFilters}
          onClearAllFilters={clearAllFilters}
          onClose={closeFiltersSidebar}
        />
      </FiltersSidePanel>

      <AppDialog
        header="Търсене по номер на резервация"
        visible={isSearchDialogOpen}
        headerClassName={styles.search_dialog_header}
        onHide={closeSearchDialog}
      >
        <FormProvider {...searchFormMethods}>
          <form
            onSubmit={searchFormMethods.handleSubmit(handleSearchReservation)}
            className={styles.search_dialog_form}
          >
            <DefaultInputField
              name="reservationId"
              label="Номер на резервация"
              placeholder="Въведете номер..."
              type="number"
            />
            <ColorButton
              label="Търси"
              type="submit"
              backgroundColorHex="var(--accent-from, #7877c6)"
              textColorHex="var(--accent-text, #ffffff)"
              loading={isSearching}
              disabled={isSearching}
            />
          </form>
        </FormProvider>
      </AppDialog>

      <BulkAssignInstructorDialog
        visible={isBulkAssignInstructorDialogOpen}
        employees={studioEmployees}
        onHide={closeBulkAssignInstructorDialog}
      />

      <BulkDeleteEventsDialog
        visible={isBulkDeleteEventsDialogOpen}
        onHide={closeBulkDeleteEventsDialog}
      />

      <HandoverNoteDialog
        visible={handover.isDialogOpen}
        selectedDate={selectedValue}
        entries={handover.entries}
        draft={handover.draft}
        hasDraft={handover.hasDraft}
        canSubmit={handover.canSubmit}
        isSaving={handover.isSaving}
        onDraftChange={handover.setDraft}
        onSubmit={handover.addEntry}
        onComposerKeyDown={handover.handleComposerKeyDown}
        onDelete={handover.deleteEntry}
        onHide={handover.closeDialog}
      />
    </div>
  );
};
